import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  FlatList,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { Input, Button, Toggle } from '../design-system';
import { RichTextEditor } from '../design-system/RichTextEditor';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { apiService } from '../../services/api';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export type ConditionField =
  | 'time'
  | 'day_of_week'
  | 'contact.isReturning'
  | 'contact.messageCount'
  | 'contact.hoursSinceLastMessage'
  | 'contact.country'
  | 'contact.tags'
  | 'contact.isAssigned'
  | 'contact.assignedRole'
  | 'formula';

export type ConditionOperator =
  | 'equals' | 'not_equals'
  | 'greater_than' | 'less_than' | 'between'
  | 'contains' | 'not_contains'
  | 'in' | 'not_in'
  | 'has_value' | 'is_empty'
  | 'matches_formula';

export interface DispatchRuleCondition {
  // Either one of the fixed ConditionField values, or a generic "contact.profile.<path>"
  // dotted path into the contact's raw profile (including custom fields).
  field: ConditionField | string;
  operator: ConditionOperator;
  value: any; // unused for has_value/is_empty
}

export type DispatchRuleOutcomeAction = 'dispatch_item' | 'dispatch_dataset' | 'override_text' | 'override_config' | 'no_action';

export interface DispatchRuleOutcome {
  action: DispatchRuleOutcomeAction;
  itemId?: string;
  setId?: string;
  text?: string;
  configPatch?: Record<string, any>;
}

export interface DispatchRuleConditionGroup {
  conditions: DispatchRuleCondition[];
  logic: 'AND' | 'OR';
  then: DispatchRuleOutcome;
}

export interface DispatchRuleFormData {
  name: string;
  channelIdentifier?: string;
  conditionGroups: DispatchRuleConditionGroup[];
  defaultOutcome?: DispatchRuleOutcome;
  isActive: boolean;
}

export interface DispatchRuleFormProps {
  initialData?: Partial<DispatchRuleFormData> & { _id?: string };
  scope: 'reply_item' | 'dataset' | 'channel_settings';
  attachedTo: string;
  /** Which channel's phone numbers to offer in the number picker. Defaults to 'whatsapp'
   * since that's the only channel with a phone-number registry surfaced in the app today. */
  channel?: 'whatsapp' | 'facebook_messenger' | 'x_twitter';
  onSubmit: (data: DispatchRuleFormData) => void;
  onCancel?: () => void;
  submitLabel?: string;
  loading?: boolean;
  hideActions?: boolean;
  submitRef?: React.MutableRefObject<(() => void) | null>;
}

const FIELD_OPTIONS: Array<{ value: ConditionField; label: string; description: string }> = [
  { value: 'time', label: 'Time of Day', description: 'Pick a clock time (business timezone)' },
  { value: 'day_of_week', label: 'Day of Week', description: 'Pick one or more days' },
  { value: 'contact.isReturning', label: 'Returning Contact', description: 'Has messaged before' },
  { value: 'contact.messageCount', label: 'Message Count', description: 'Total messages from contact' },
  { value: 'contact.hoursSinceLastMessage', label: 'Hours Since Last Message', description: 'Time since last inbound message' },
  { value: 'contact.country', label: 'Country', description: 'Pick from a list, derived from the contact\'s phone number' },
  { value: 'contact.tags', label: 'Contact Tags', description: 'Tags on the contact profile' },
  { value: 'contact.isAssigned', label: 'Is Assigned', description: 'Contact is assigned to a user/department' },
  { value: 'contact.assignedRole', label: 'Assigned Role', description: 'Role of assigned user/department' },
  { value: 'formula', label: 'Custom Formula', description: 'Math/logic expression, e.g. messageCount > 5 and hoursSinceLastMessage < 24' },
];

// Sentinel picker option — not a real field value, selecting it switches the condition row
// into free-text "custom field path" entry mode instead of setting condition.field directly.
const CUSTOM_FIELD_SENTINEL = '__custom_field__';
const CUSTOM_FIELD_PREFIX = 'contact.profile.';
const isCustomFieldPath = (field: string) => field.startsWith(CUSTOM_FIELD_PREFIX);

/** ISO 3166-1 alpha-2 country codes + display names, for the contact.country condition
 * picker. The rule engine (rule-engine.service.ts on the backend) derives ISO codes from
 * the contact's phone number, so this list must store ISO codes as the condition value —
 * not calling codes — or the condition will never match. */
const COUNTRY_OPTIONS: Array<{ code: string; name: string }> = [
  { code: 'EG', name: 'Egypt' }, { code: 'US', name: 'United States' }, { code: 'GB', name: 'United Kingdom' },
  { code: 'SA', name: 'Saudi Arabia' }, { code: 'AE', name: 'United Arab Emirates' }, { code: 'KW', name: 'Kuwait' },
  { code: 'QA', name: 'Qatar' }, { code: 'BH', name: 'Bahrain' }, { code: 'OM', name: 'Oman' }, { code: 'JO', name: 'Jordan' },
  { code: 'LB', name: 'Lebanon' }, { code: 'IQ', name: 'Iraq' }, { code: 'SY', name: 'Syria' }, { code: 'YE', name: 'Yemen' },
  { code: 'PS', name: 'Palestine' }, { code: 'MA', name: 'Morocco' }, { code: 'DZ', name: 'Algeria' }, { code: 'TN', name: 'Tunisia' },
  { code: 'LY', name: 'Libya' }, { code: 'SD', name: 'Sudan' }, { code: 'DE', name: 'Germany' }, { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' }, { code: 'ES', name: 'Spain' }, { code: 'NL', name: 'Netherlands' }, { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' }, { code: 'AT', name: 'Austria' }, { code: 'SE', name: 'Sweden' }, { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' }, { code: 'FI', name: 'Finland' }, { code: 'PL', name: 'Poland' }, { code: 'GR', name: 'Greece' },
  { code: 'PT', name: 'Portugal' }, { code: 'IE', name: 'Ireland' }, { code: 'TR', name: 'Turkey' }, { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' }, { code: 'CA', name: 'Canada' }, { code: 'MX', name: 'Mexico' }, { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' }, { code: 'CO', name: 'Colombia' }, { code: 'CL', name: 'Chile' }, { code: 'PE', name: 'Peru' },
  { code: 'IN', name: 'India' }, { code: 'PK', name: 'Pakistan' }, { code: 'BD', name: 'Bangladesh' }, { code: 'CN', name: 'China' },
  { code: 'JP', name: 'Japan' }, { code: 'KR', name: 'South Korea' }, { code: 'ID', name: 'Indonesia' }, { code: 'MY', name: 'Malaysia' },
  { code: 'SG', name: 'Singapore' }, { code: 'PH', name: 'Philippines' }, { code: 'TH', name: 'Thailand' }, { code: 'VN', name: 'Vietnam' },
  { code: 'AU', name: 'Australia' }, { code: 'NZ', name: 'New Zealand' }, { code: 'ZA', name: 'South Africa' }, { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' }, { code: 'GH', name: 'Ghana' }, { code: 'ET', name: 'Ethiopia' },
];

const OPERATOR_OPTIONS: Array<{ value: ConditionOperator; label: string }> = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'between', label: 'Between' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does Not Contain' },
  { value: 'in', label: 'In List' },
  { value: 'not_in', label: 'Not In List' },
  { value: 'has_value', label: 'Has a Value' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'matches_formula', label: 'Matches Formula' },
];

const OUTCOME_ACTION_OPTIONS: Array<{ value: DispatchRuleOutcomeAction; label: string; icon: string; description: string }> = [
  { value: 'dispatch_item', label: 'Send Reply Item', icon: 'chatbubble-outline', description: 'Dispatch a specific quick reply item' },
  { value: 'dispatch_dataset', label: 'Send Dataset', icon: 'layers-outline', description: 'Dispatch a replies dataset' },
  { value: 'override_text', label: 'Override Text', icon: 'create-outline', description: 'Send custom text instead' },
  { value: 'override_config', label: 'Edit This Item', icon: 'options-outline', description: 'Keep the same item, but swap its text/media for this condition' },
  { value: 'no_action', label: 'Do Nothing', icon: 'close-circle-outline', description: 'Suppress the response' },
];

const emptyCondition = (): DispatchRuleCondition => ({ field: 'contact.messageCount', operator: 'greater_than', value: '' });
const emptyOutcome = (): DispatchRuleOutcome => ({ action: 'no_action' });
const emptyGroup = (): DispatchRuleConditionGroup => ({ conditions: [emptyCondition()], logic: 'AND', then: emptyOutcome() });

export const DispatchRuleForm: React.FC<DispatchRuleFormProps> = ({
  initialData,
  channel = 'whatsapp',
  onSubmit,
  onCancel,
  submitLabel = 'Save Rule',
  loading = false,
  hideActions = false,
  submitRef,
}) => {
  const styles = useThemedStyles(getStyles);
  const [formData, setFormData] = useState<DispatchRuleFormData>({
    name: initialData?.name || '',
    channelIdentifier: initialData?.channelIdentifier || undefined,
    conditionGroups: initialData?.conditionGroups?.length ? initialData.conditionGroups : [emptyGroup()],
    defaultOutcome: initialData?.defaultOutcome || emptyOutcome(),
    isActive: initialData?.isActive !== undefined ? initialData.isActive : true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [availableItems, setAvailableItems] = useState<Array<{ _id: string; displayText: string }>>([]);
  const [availableSets, setAvailableSets] = useState<Array<{ _id: string; name: string }>>([]);
  const [availableNumbers, setAvailableNumbers] = useState<Array<{ id: string; label: string }>>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [fieldPickerFor, setFieldPickerFor] = useState<{ groupIndex: number; conditionIndex: number } | null>(null);
  const [operatorPickerFor, setOperatorPickerFor] = useState<{ groupIndex: number; conditionIndex: number } | null>(null);
  const [actionPickerFor, setActionPickerFor] = useState<'default' | number | null>(null);
  const [itemPickerFor, setItemPickerFor] = useState<'default' | number | null>(null);
  const [setPickerFor, setSetPickerFor] = useState<'default' | number | null>(null);
  const [numberPickerOpen, setNumberPickerOpen] = useState(false);
  const [countryPickerFor, setCountryPickerFor] = useState<{ groupIndex: number; conditionIndex: number } | null>(null);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [setSearchQuery, setSetSearchQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoadingOptions(true);
        const [itemsRes, setsRes, numbersRes] = await Promise.all([
          apiService.getQuickReplyItems({ limit: 1000, skip: 0 }),
          apiService.getQuickReplySets({ limit: 1000, skip: 0 }),
          channel === 'whatsapp' ? apiService.getWhatsAppPhoneNumbers() : Promise.resolve(null),
        ]);
        if (itemsRes.statusCode === 1000 && itemsRes.data?.items) {
          setAvailableItems(itemsRes.data.items.map((i: any) => ({ _id: i._id, displayText: i.displayText || 'Unnamed Item' })));
        }
        if (setsRes.statusCode === 1000 && setsRes.data?.quickReplySets) {
          setAvailableSets(setsRes.data.quickReplySets.map((s: any) => ({ _id: s._id, name: s.name || 'Unnamed Set' })));
        }
        if (numbersRes && numbersRes.statusCode === 1000 && Array.isArray(numbersRes.data)) {
          setAvailableNumbers(
            numbersRes.data
              .filter((p: any) => p.whatsapp_phone_number_id)
              .map((p: any) => ({ id: p.whatsapp_phone_number_id, label: p.whatsapp_phone_number || p.whatsapp_phone_number_id }))
          );
        }
      } catch (error) {
        console.error('Error loading dispatch rule options:', error);
      } finally {
        setLoadingOptions(false);
      }
    })();
  }, [channel]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Rule name is required';
    }
    formData.conditionGroups.forEach((group, gi) => {
      if (group.conditions.length === 0) {
        newErrors[`group.${gi}`] = 'Each condition group needs at least one condition';
      }
      group.conditions.forEach((cond, ci) => {
        if (cond.value === '' || cond.value === undefined || cond.value === null) {
          newErrors[`group.${gi}.condition.${ci}`] = 'Condition value is required';
        }
      });
      if (group.then.action === 'dispatch_item' && !group.then.itemId) {
        newErrors[`group.${gi}.outcome`] = 'Select a reply item';
      }
      if (group.then.action === 'dispatch_dataset' && !group.then.setId) {
        newErrors[`group.${gi}.outcome`] = 'Select a dataset';
      }
      if (group.then.action === 'override_text' && !group.then.text?.trim()) {
        newErrors[`group.${gi}.outcome`] = 'Enter override text';
      }
      if (group.then.action === 'override_config' && !group.then.configPatch?.fixedMessage?.text?.trim()) {
        newErrors[`group.${gi}.outcome`] = 'Enter text for this condition';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
    }
  };

  React.useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const updateGroup = (groupIndex: number, updater: (group: DispatchRuleConditionGroup) => DispatchRuleConditionGroup) => {
    setFormData((prev) => ({
      ...prev,
      conditionGroups: prev.conditionGroups.map((g, i) => (i === groupIndex ? updater(g) : g)),
    }));
  };

  const addGroup = () => {
    setFormData((prev) => ({ ...prev, conditionGroups: [...prev.conditionGroups, emptyGroup()] }));
  };

  const removeGroup = (groupIndex: number) => {
    setFormData((prev) => ({ ...prev, conditionGroups: prev.conditionGroups.filter((_, i) => i !== groupIndex) }));
  };

  const addCondition = (groupIndex: number) => {
    updateGroup(groupIndex, (g) => ({ ...g, conditions: [...g.conditions, emptyCondition()] }));
  };

  const removeCondition = (groupIndex: number, conditionIndex: number) => {
    updateGroup(groupIndex, (g) => ({ ...g, conditions: g.conditions.filter((_, i) => i !== conditionIndex) }));
  };

  const updateCondition = (groupIndex: number, conditionIndex: number, patch: Partial<DispatchRuleCondition>) => {
    updateGroup(groupIndex, (g) => ({
      ...g,
      conditions: g.conditions.map((c, i) => (i === conditionIndex ? { ...c, ...patch } : c)),
    }));
  };

  const updateOutcome = (groupIndex: number, patch: Partial<DispatchRuleOutcome>) => {
    updateGroup(groupIndex, (g) => ({ ...g, then: { ...g.then, ...patch } }));
  };

  const updateDefaultOutcome = (patch: Partial<DispatchRuleOutcome>) => {
    setFormData((prev) => ({ ...prev, defaultOutcome: { ...(prev.defaultOutcome || emptyOutcome()), ...patch } }));
  };

  const needsValueTextInput = (operator: ConditionOperator) =>
    operator !== 'between';

  const renderValueInput = (groupIndex: number, conditionIndex: number, condition: DispatchRuleCondition) => {
    const errorKey = `group.${groupIndex}.condition.${conditionIndex}`;

    if (condition.operator === 'has_value' || condition.operator === 'is_empty') {
      return null; // these operators don't need a value typed in
    }

    // "time" resolves server-side to minutes-since-midnight (0-1439) in the business
    // timezone — a plain numeric box invites entering "14" meaning 2pm and silently
    // comparing against the wrong scale, so we always collect a clock time and convert.
    const minutesToClock = (mins: any) => {
      const n = Number(mins);
      if (!Number.isFinite(n) || n < 0) return '';
      const h = Math.floor(n / 60) % 24;
      const m = Math.floor(n % 60);
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };
    const clockToMinutes = (clock: string): number | '' => {
      const match = clock.match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return '';
      const h = Math.min(23, Math.max(0, parseInt(match[1], 10) || 0));
      const m = Math.min(59, Math.max(0, parseInt(match[2], 10) || 0));
      return h * 60 + m;
    };

    if (condition.field === 'time' && condition.operator === 'between') {
      const [min, max] = Array.isArray(condition.value) ? condition.value : ['', ''];
      return (
        <View style={styles.betweenRow}>
          <View style={styles.betweenInput}>
            <Input
              placeholder="09:00"
              value={minutesToClock(min)}
              onChangeText={(text) => updateCondition(groupIndex, conditionIndex, { value: [clockToMinutes(text), max] })}
              size="sm"
            />
          </View>
          <Text style={styles.betweenSeparator}>and</Text>
          <View style={styles.betweenInput}>
            <Input
              placeholder="17:00"
              value={minutesToClock(max)}
              onChangeText={(text) => updateCondition(groupIndex, conditionIndex, { value: [min, clockToMinutes(text)] })}
              size="sm"
            />
          </View>
        </View>
      );
    }
    if (condition.field === 'time') {
      return (
        <Input
          placeholder="14:30"
          value={minutesToClock(condition.value)}
          onChangeText={(text) => updateCondition(groupIndex, conditionIndex, { value: clockToMinutes(text) })}
          size="sm"
          error={errors[errorKey]}
        />
      );
    }
    if (condition.field === 'day_of_week') {
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      return (
        <View style={styles.dayRow}>
          {days.map((day) => (
            <TouchableOpacity
              key={day}
              style={[styles.dayOption, condition.value === day && styles.dayOptionActive]}
              onPress={() => updateCondition(groupIndex, conditionIndex, { value: day })}
            >
              <Text style={[styles.dayOptionText, condition.value === day && styles.dayOptionTextActive]}>{day}</Text>
            </TouchableOpacity>
          ))}
        </View>
      );
    }
    if (condition.field === 'contact.country') {
      const selected = COUNTRY_OPTIONS.find((c) => c.code === condition.value);
      return (
        <TouchableOpacity
          style={styles.selectButton}
          onPress={() => setCountryPickerFor({ groupIndex, conditionIndex })}
        >
          <Text style={[styles.selectButtonText, !condition.value && styles.selectButtonPlaceholder]}>
            {selected ? selected.name : 'Select country'}
          </Text>
          <Ionicons name="chevron-down" size={20} color={colors.textLight} />
        </TouchableOpacity>
      );
    }
    if (condition.operator === 'between') {
      const [min, max] = Array.isArray(condition.value) ? condition.value : ['', ''];
      return (
        <View style={styles.betweenRow}>
          <View style={styles.betweenInput}>
            <Input
              placeholder="Min"
              value={String(min ?? '')}
              onChangeText={(text) => updateCondition(groupIndex, conditionIndex, { value: [text, max] })}
              keyboardType="numeric"
              size="sm"
            />
          </View>
          <Text style={styles.betweenSeparator}>and</Text>
          <View style={styles.betweenInput}>
            <Input
              placeholder="Max"
              value={String(max ?? '')}
              onChangeText={(text) => updateCondition(groupIndex, conditionIndex, { value: [min, text] })}
              keyboardType="numeric"
              size="sm"
            />
          </View>
        </View>
      );
    }
    if (condition.field === 'formula' || condition.operator === 'matches_formula') {
      return (
        <Input
          placeholder="e.g. messageCount > 5 and hoursSinceLastMessage < 24"
          value={String(condition.value ?? '')}
          onChangeText={(text) => updateCondition(groupIndex, conditionIndex, { value: text })}
          size="sm"
          error={errors[errorKey]}
        />
      );
    }
    if (condition.field === 'contact.isReturning' || condition.field === 'contact.isAssigned') {
      return (
        <View style={styles.booleanRow}>
          {['true', 'false'].map((v) => (
            <TouchableOpacity
              key={v}
              style={[styles.booleanOption, String(condition.value) === v && styles.booleanOptionActive]}
              onPress={() => updateCondition(groupIndex, conditionIndex, { value: v === 'true' })}
            >
              <Text style={[styles.booleanOptionText, String(condition.value) === v && styles.booleanOptionTextActive]}>
                {v === 'true' ? 'Yes' : 'No'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      );
    }
    return (
      <Input
        placeholder={condition.operator === 'in' || condition.operator === 'not_in' ? 'comma,separated,values' : 'Value'}
        value={Array.isArray(condition.value) ? condition.value.join(',') : String(condition.value ?? '')}
        onChangeText={(text) => {
          const value = condition.operator === 'in' || condition.operator === 'not_in'
            ? text.split(',').map((v) => v.trim()).filter(Boolean)
            : text;
          updateCondition(groupIndex, conditionIndex, { value });
        }}
        keyboardType={condition.field === 'contact.messageCount' || condition.field === 'contact.hoursSinceLastMessage' ? 'numeric' : 'default'}
        size="sm"
        error={errors[errorKey]}
      />
    );
  };

  const renderOutcomePicker = (groupIndex: number | 'default', outcome: DispatchRuleOutcome, onChange: (patch: Partial<DispatchRuleOutcome>) => void) => {
    const errorKey = groupIndex === 'default' ? 'defaultOutcome' : `group.${groupIndex}.outcome`;
    const actionMeta = OUTCOME_ACTION_OPTIONS.find((o) => o.value === outcome.action);
    return (
      <View style={styles.outcomeBlock}>
        <Text style={styles.label}>Then</Text>
        <TouchableOpacity
          style={styles.selectButton}
          onPress={() => setActionPickerFor(groupIndex)}
        >
          <View style={styles.typeSelectorContent}>
            <Ionicons name={(actionMeta?.icon || 'help-outline') as any} size={18} color={colors.primary} />
            <Text style={styles.selectButtonText}>{actionMeta?.label || 'Select outcome'}</Text>
          </View>
          <Ionicons name="chevron-down" size={20} color={colors.textLight} />
        </TouchableOpacity>

        {outcome.action === 'dispatch_item' && (
          <TouchableOpacity style={[styles.selectButton, styles.outcomeSubField]} onPress={() => setItemPickerFor(groupIndex)}>
            <Text style={[styles.selectButtonText, !outcome.itemId && styles.selectButtonPlaceholder]}>
              {outcome.itemId ? availableItems.find((i) => i._id === outcome.itemId)?.displayText || 'Item selected' : 'Select reply item'}
            </Text>
            <Ionicons name="chevron-down" size={20} color={colors.textLight} />
          </TouchableOpacity>
        )}

        {outcome.action === 'dispatch_dataset' && (
          <TouchableOpacity style={[styles.selectButton, styles.outcomeSubField]} onPress={() => setSetPickerFor(groupIndex)}>
            <Text style={[styles.selectButtonText, !outcome.setId && styles.selectButtonPlaceholder]}>
              {outcome.setId ? availableSets.find((s) => s._id === outcome.setId)?.name || 'Dataset selected' : 'Select dataset'}
            </Text>
            <Ionicons name="chevron-down" size={20} color={colors.textLight} />
          </TouchableOpacity>
        )}

        {outcome.action === 'override_text' && (
          <View style={styles.outcomeSubField}>
            <RichTextEditor
              value={outcome.text || ''}
              onChange={(text) => onChange({ text })}
              placeholder="Text to send instead"
              maxLength={4096}
            />
          </View>
        )}

        {outcome.action === 'override_config' && (
          <View style={styles.outcomeSubField}>
            <Text style={styles.helperText}>
              Keeps the same item, but swaps its text for this condition. Media/buttons stay unchanged.
            </Text>
            <RichTextEditor
              value={outcome.configPatch?.fixedMessage?.text || ''}
              onChange={(text) => onChange({ configPatch: { ...(outcome.configPatch || {}), fixedMessage: { ...(outcome.configPatch?.fixedMessage || {}), text } } })}
              placeholder="Text for this condition"
              maxLength={4096}
            />
          </View>
        )}

        {errors[errorKey] && <Text style={styles.errorText}>{errors[errorKey]}</Text>}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.form}>
        <View style={styles.field}>
          <Input
            label="Rule Name *"
            value={formData.name}
            onChangeText={(text) => setFormData((prev) => ({ ...prev, name: text }))}
            placeholder="e.g. Business hours routing"
            error={errors.name}
          />
        </View>

        <View style={styles.field}>
          <Toggle
            label="Active"
            description="Inactive rules are skipped and default dispatch behavior applies"
            value={formData.isActive}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, isActive: value }))}
          />
        </View>

        {channel === 'whatsapp' && (
          <View style={styles.field}>
            <Text style={styles.label}>Applies To</Text>
            <TouchableOpacity style={styles.selectButton} onPress={() => setNumberPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !formData.channelIdentifier && styles.selectButtonPlaceholder]}>
                {formData.channelIdentifier
                  ? availableNumbers.find((n) => n.id === formData.channelIdentifier)?.label || 'Number selected'
                  : 'All numbers'}
              </Text>
              <Ionicons name="chevron-down" size={20} color={colors.textLight} />
            </TouchableOpacity>
            <Text style={styles.helperText}>
              Leave as "All numbers" to apply this rule to every WhatsApp number on this account, or pick one to scope it to a single number.
            </Text>
          </View>
        )}

        <Text style={styles.sectionTitle}>Condition Groups</Text>
        <Text style={styles.helperText}>
          Evaluated in order — the first group whose conditions match wins. If none match, the default outcome below applies.
        </Text>

        {formData.conditionGroups.map((group, groupIndex) => (
          <View key={groupIndex} style={styles.groupCard}>
            <View style={styles.groupHeader}>
              <Text style={styles.groupTitle}>Group {groupIndex + 1}</Text>
              {formData.conditionGroups.length > 1 && (
                <TouchableOpacity onPress={() => removeGroup(groupIndex)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                  <Ionicons name="trash-outline" size={20} color={colors.error} />
                </TouchableOpacity>
              )}
            </View>

            <View style={styles.logicToggleRow}>
              <Text style={styles.helperText}>Match</Text>
              {(['AND', 'OR'] as const).map((logic) => (
                <TouchableOpacity
                  key={logic}
                  style={[styles.logicOption, group.logic === logic && styles.logicOptionActive]}
                  onPress={() => updateGroup(groupIndex, (g) => ({ ...g, logic }))}
                >
                  <Text style={[styles.logicOptionText, group.logic === logic && styles.logicOptionTextActive]}>{logic}</Text>
                </TouchableOpacity>
              ))}
              <Text style={styles.helperText}>of the following conditions</Text>
            </View>

            {group.conditions.map((condition, conditionIndex) => {
              const fieldMeta = FIELD_OPTIONS.find((f) => f.value === condition.field);
              const operatorMeta = OPERATOR_OPTIONS.find((o) => o.value === condition.operator);
              return (
                <View key={conditionIndex} style={styles.conditionRow}>
                  <View style={styles.conditionRowHeader}>
                    <Text style={styles.conditionIndexLabel}>#{conditionIndex + 1}</Text>
                    {group.conditions.length > 1 && (
                      <TouchableOpacity onPress={() => removeCondition(groupIndex, conditionIndex)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                        <Ionicons name="close-circle" size={18} color={colors.error} />
                      </TouchableOpacity>
                    )}
                  </View>

                  {isCustomFieldPath(String(condition.field)) ? (
                    <View style={styles.customFieldRow}>
                      <View style={{ flex: 1 }}>
                        <Input
                          placeholder="e.g. email or customFields.birthday"
                          value={String(condition.field).slice(CUSTOM_FIELD_PREFIX.length)}
                          onChangeText={(text) => updateCondition(groupIndex, conditionIndex, { field: `${CUSTOM_FIELD_PREFIX}${text}` })}
                          size="sm"
                          autoCapitalize="none"
                        />
                      </View>
                      <TouchableOpacity
                        style={styles.customFieldChangeButton}
                        onPress={() => setFieldPickerFor({ groupIndex, conditionIndex })}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <Ionicons name="swap-horizontal-outline" size={18} color={colors.primary} />
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <TouchableOpacity style={styles.selectButton} onPress={() => setFieldPickerFor({ groupIndex, conditionIndex })}>
                      <Text style={styles.selectButtonText}>{fieldMeta?.label || 'Select field'}</Text>
                      <Ionicons name="chevron-down" size={20} color={colors.textLight} />
                    </TouchableOpacity>
                  )}

                  {condition.field !== 'formula' && (
                    <TouchableOpacity
                      style={[styles.selectButton, styles.conditionSubField]}
                      onPress={() => setOperatorPickerFor({ groupIndex, conditionIndex })}
                    >
                      <Text style={styles.selectButtonText}>{operatorMeta?.label || 'Select operator'}</Text>
                      <Ionicons name="chevron-down" size={20} color={colors.textLight} />
                    </TouchableOpacity>
                  )}

                  <View style={styles.conditionSubField}>
                    {renderValueInput(groupIndex, conditionIndex, condition)}
                  </View>
                </View>
              );
            })}

            <TouchableOpacity style={styles.addRowButton} onPress={() => addCondition(groupIndex)}>
              <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
              <Text style={styles.addRowButtonText}>Add Condition</Text>
            </TouchableOpacity>

            {renderOutcomePicker(groupIndex, group.then, (patch) => updateOutcome(groupIndex, patch))}
          </View>
        ))}

        <TouchableOpacity style={styles.addGroupButton} onPress={addGroup}>
          <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
          <Text style={styles.addGroupButtonText}>Add Condition Group</Text>
        </TouchableOpacity>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Default Outcome</Text>
          <Text style={styles.helperText}>Used when no condition group matches.</Text>
          {renderOutcomePicker('default', formData.defaultOutcome || emptyOutcome(), updateDefaultOutcome)}
        </View>

        {!hideActions && (
          <View style={styles.actions}>
            {!!onCancel && (
              <View style={styles.cancelButton}>
                <Button title="Cancel" variant="outline" onPress={onCancel} fullWidth />
              </View>
            )}
            <View style={styles.submitButton}>
              <Button title={submitLabel} variant="primary" onPress={handleSubmit} loading={loading} fullWidth />
            </View>
          </View>
        )}
      </View>

      {/* Field Picker Modal */}
      <Modal visible={!!fieldPickerFor} animationType="slide" transparent onRequestClose={() => setFieldPickerFor(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Field</Text>
              <TouchableOpacity onPress={() => setFieldPickerFor(null)}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            <FlatList
              data={[...FIELD_OPTIONS, { value: CUSTOM_FIELD_SENTINEL, label: 'Custom Contact Field', description: 'Any other contact field, e.g. email or a custom field like customFields.birthday' }]}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.typeOption}
                  onPress={() => {
                    if (fieldPickerFor) {
                      if (item.value === CUSTOM_FIELD_SENTINEL) {
                        updateCondition(fieldPickerFor.groupIndex, fieldPickerFor.conditionIndex, { field: CUSTOM_FIELD_PREFIX, operator: 'has_value', value: '' });
                      } else {
                        const defaultOperator: ConditionOperator = item.value === 'formula' ? 'matches_formula' : 'equals';
                        updateCondition(fieldPickerFor.groupIndex, fieldPickerFor.conditionIndex, { field: item.value, operator: defaultOperator, value: '' });
                      }
                    }
                    setFieldPickerFor(null);
                  }}
                >
                  <View style={styles.typeOptionContent}>
                    <Text style={styles.typeOptionLabel}>{item.label}</Text>
                    <Text style={styles.typeOptionDescription}>{item.description}</Text>
                  </View>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Operator Picker Modal */}
      <Modal visible={!!operatorPickerFor} animationType="slide" transparent onRequestClose={() => setOperatorPickerFor(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Operator</Text>
              <TouchableOpacity onPress={() => setOperatorPickerFor(null)}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            <FlatList
              data={OPERATOR_OPTIONS}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.typeOption}
                  onPress={() => {
                    if (operatorPickerFor) {
                      updateCondition(operatorPickerFor.groupIndex, operatorPickerFor.conditionIndex, { operator: item.value, value: '' });
                    }
                    setOperatorPickerFor(null);
                  }}
                >
                  <View style={styles.typeOptionContent}>
                    <Text style={styles.typeOptionLabel}>{item.label}</Text>
                  </View>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Outcome Action Picker Modal */}
      <Modal visible={actionPickerFor !== null} animationType="slide" transparent onRequestClose={() => setActionPickerFor(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Outcome</Text>
              <TouchableOpacity onPress={() => setActionPickerFor(null)}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            <FlatList
              data={OUTCOME_ACTION_OPTIONS}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.typeOption}
                  onPress={() => {
                    if (actionPickerFor === 'default') {
                      updateDefaultOutcome({ action: item.value, itemId: undefined, setId: undefined, text: undefined });
                    } else if (actionPickerFor !== null) {
                      updateOutcome(actionPickerFor, { action: item.value, itemId: undefined, setId: undefined, text: undefined });
                    }
                    setActionPickerFor(null);
                  }}
                >
                  <Ionicons name={item.icon as any} size={22} color={colors.primary} />
                  <View style={styles.typeOptionContent}>
                    <Text style={styles.typeOptionLabel}>{item.label}</Text>
                    <Text style={styles.typeOptionDescription}>{item.description}</Text>
                  </View>
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>

      {/* Item Picker Modal */}
      <Modal visible={itemPickerFor !== null} animationType="slide" transparent onRequestClose={() => { setItemPickerFor(null); setItemSearchQuery(''); }}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Reply Item</Text>
              <TouchableOpacity onPress={() => { setItemPickerFor(null); setItemSearchQuery(''); }}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search items..."
                value={itemSearchQuery}
                onChangeText={setItemSearchQuery}
                placeholderTextColor={colors.textLight}
              />
            </View>
            {loadingOptions ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            ) : (
              <FlatList
                data={availableItems.filter((i) => i.displayText.toLowerCase().includes(itemSearchQuery.toLowerCase()))}
                keyExtractor={(item) => item._id}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={styles.typeOption}
                    onPress={() => {
                      if (itemPickerFor === 'default') {
                        updateDefaultOutcome({ itemId: item._id });
                      } else if (itemPickerFor !== null) {
                        updateOutcome(itemPickerFor, { itemId: item._id });
                      }
                      setItemPickerFor(null);
                      setItemSearchQuery('');
                    }}
                  >
                    <View style={styles.typeOptionContent}>
                      <Text style={styles.typeOptionLabel}>{item.displayText}</Text>
                    </View>
                  </TouchableOpacity>
                )}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>No items found</Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>

      {/* Dataset Picker Modal */}
      <Modal visible={setPickerFor !== null} animationType="slide" transparent onRequestClose={() => { setSetPickerFor(null); setSetSearchQuery(''); }}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Dataset</Text>
              <TouchableOpacity onPress={() => { setSetPickerFor(null); setSetSearchQuery(''); }}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search datasets..."
                value={setSearchQuery}
                onChangeText={setSetSearchQuery}
                placeholderTextColor={colors.textLight}
              />
            </View>
            {loadingOptions ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            ) : (
              <FlatList
                data={availableSets.filter((s) => s.name.toLowerCase().includes(setSearchQuery.toLowerCase()))}
                keyExtractor={(item) => item._id}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={styles.typeOption}
                    onPress={() => {
                      if (setPickerFor === 'default') {
                        updateDefaultOutcome({ setId: item._id });
                      } else if (setPickerFor !== null) {
                        updateOutcome(setPickerFor, { setId: item._id });
                      }
                      setSetPickerFor(null);
                      setSetSearchQuery('');
                    }}
                  >
                    <View style={styles.typeOptionContent}>
                      <Text style={styles.typeOptionLabel}>{item.name}</Text>
                    </View>
                  </TouchableOpacity>
                )}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>No datasets found</Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>

      {/* Phone Number Picker Modal */}
      <Modal visible={numberPickerOpen} animationType="slide" transparent onRequestClose={() => setNumberPickerOpen(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Applies To</Text>
              <TouchableOpacity onPress={() => setNumberPickerOpen(false)}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            {loadingOptions ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            ) : (
              <FlatList
                data={[{ id: '', label: 'All numbers' }, ...availableNumbers]}
                keyExtractor={(item) => item.id || 'all'}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={[styles.typeOption, (formData.channelIdentifier || '') === item.id && styles.typeOptionSelected]}
                    onPress={() => {
                      setFormData((prev) => ({ ...prev, channelIdentifier: item.id || undefined }));
                      setNumberPickerOpen(false);
                    }}
                  >
                    <View style={styles.typeOptionContent}>
                      <Text style={styles.typeOptionLabel}>{item.label}</Text>
                    </View>
                    {(formData.channelIdentifier || '') === item.id && (
                      <Ionicons name="checkmark-circle" size={22} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                )}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>No numbers found</Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>

      {/* Country Picker Modal */}
      <Modal
        visible={!!countryPickerFor}
        animationType="slide"
        transparent
        onRequestClose={() => { setCountryPickerFor(null); setCountrySearchQuery(''); }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Country</Text>
              <TouchableOpacity onPress={() => { setCountryPickerFor(null); setCountrySearchQuery(''); }}>
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search countries..."
                value={countrySearchQuery}
                onChangeText={setCountrySearchQuery}
                placeholderTextColor={colors.textLight}
              />
            </View>
            <FlatList
              data={COUNTRY_OPTIONS.filter((c) => c.name.toLowerCase().includes(countrySearchQuery.toLowerCase()))}
              keyExtractor={(item) => item.code}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.typeOption}
                  onPress={() => {
                    if (countryPickerFor) {
                      updateCondition(countryPickerFor.groupIndex, countryPickerFor.conditionIndex, { value: item.code });
                    }
                    setCountryPickerFor(null);
                    setCountrySearchQuery('');
                  }}
                >
                  <View style={styles.typeOptionContent}>
                    <Text style={styles.typeOptionLabel}>{item.name}</Text>
                  </View>
                </TouchableOpacity>
              )}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>No countries found</Text>
                </View>
              }
            />
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
  },
  form: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
    ...designTokens.shadows.sm,
  },
  field: {
    marginBottom: spacing.md,
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs,
  },
  helperText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs / 2,
  },
  errorText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs / 2,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
    marginTop: spacing.lg,
    marginBottom: spacing.xs,
  },
  section: {
    marginTop: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  groupCard: {
    marginTop: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  groupTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  logicToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.md,
    flexWrap: 'wrap',
  },
  logicOption: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  logicOptionActive: {
    borderColor: colors.primary,
    backgroundColor: `${String(colors.primary)}15`,
  },
  logicOptionText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  logicOptionTextActive: {
    color: colors.primary,
  },
  conditionRow: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.sm,
    marginBottom: spacing.sm,
  },
  conditionRowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  conditionIndexLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
  },
  conditionSubField: {
    marginTop: spacing.sm,
  },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
  },
  selectButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
  },
  selectButtonPlaceholder: {
    color: colors.textLight,
  },
  customFieldRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  customFieldChangeButton: { padding: spacing.xs },
  typeSelectorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  betweenRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  betweenInput: {
    flex: 1,
  },
  betweenSeparator: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  booleanRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  booleanOption: {
    flex: 1,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    alignItems: 'center',
  },
  booleanOptionActive: {
    borderColor: colors.primary,
    backgroundColor: `${String(colors.primary)}15`,
  },
  booleanOptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  booleanOptionTextActive: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  dayRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  dayOption: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: colors.background.surface,
    minWidth: 44,
    alignItems: 'center',
  },
  dayOptionActive: {
    borderColor: colors.primary,
    backgroundColor: `${String(colors.primary)}15`,
  },
  dayOptionText: {
    fontSize: typography.fontSize.xs,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  dayOptionTextActive: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  addRowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
  },
  addRowButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  addGroupButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    marginTop: spacing.md,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: designTokens.borderRadius.md,
    borderStyle: 'dashed',
  },
  addGroupButtonText: {
    fontSize: typography.fontSize.base,
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  outcomeBlock: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  outcomeSubField: {
    marginTop: spacing.sm,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  cancelButton: {
    flex: 1,
  },
  submitButton: {
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.lg,
    borderTopRightRadius: designTokens.borderRadius.lg,
    maxHeight: '80%',
    paddingBottom: spacing.lg,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  searchIcon: {
    marginRight: spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
    paddingVertical: spacing.xs,
  },
  typeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: `${String(colors.border)}40`,
    gap: spacing.md,
  },
  typeOptionSelected: {
    backgroundColor: `${String(colors.primary)}10`,
  },
  typeOptionContent: {
    flex: 1,
  },
  typeOptionLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  typeOptionDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  emptyContainer: {
    padding: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
});
